import type { ReactNode } from 'react';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { BiCalendar } from 'react-icons/bi';
import { LuArrowUpRight } from 'react-icons/lu';
import type { TourCardData } from '../../utils/tour-card-data';
import { formatTourPrice } from '../../utils/tour-price-utils';
import { formatTourDateRange as formatDateRange } from '../../utils/tour-date-utils';

export interface TourCardProps {
  tour?: TourCardData;
  priority?: boolean;
  headingTag?: 'h2' | 'h3';
  className?: { card?: string };
}
export type TourCardLabels = {
  past: string;
  discount: string;
  from: string;
  noDate: string;
  more: string;
};

export function stripTourDescriptionBlocks(html: string): string {
  return html
    .replace(/<\/?(p|br|div|h[1-6]|ul|ol|li|blockquote|pre|hr)(\s[^>]*)?\/?>/gi, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
export function getTourTermLabel(count: number, locale: string): string {
  if (count === 1) return 'termín';
  if (count >= 2 && count <= 4) return 'termíny';
  return locale === 'cs' ? 'termínů' : 'termínov';
}
const formatDiscount = (discount: number) => Math.ceil(discount);

// Shared tenant markup: server homepage and stateful client listing.
// Rich text is composed by the caller; never bypass its sanitizer.
export function TourCardView({
  tour,
  className,
  priority = false,
  headingTag: Title = 'h3',
  currency,
  locale,
  labels,
  description,
}: TourCardProps & {
  currency: string;
  locale: string;
  labels: TourCardLabels;
  description: ReactNode;
}) {
  const isPastTour = (tour?.valid_tour_dates_count ?? 0) === 0;
  return (
    <Link
      prefetch={false}
      href={tour?.slug ?? '/'}
      className={cn(
        'col-span-1 flex w-full flex-col items-start justify-start overflow-hidden rounded-[12px]',
        className?.card,
      )}
    >
      <div className="relative h-[187px] w-full sm:h-[200px] lg:h-[232px]">
        {isPastTour ? (
          <span className="absolute left-2 top-2 z-[2] rounded-[12px] bg-accent/90 px-4 py-2 text-sm font-semibold text-white lg:text-base">
            {labels.past}
          </span>
        ) : (
          !!tour?.cheapest_tour_date?.discount && (
            <span className="absolute left-2 top-4 z-[2] rounded-[12px] bg-accent/75 px-4 py-2 text-sm font-semibold text-white lg:text-base">
              -{formatDiscount(tour.cheapest_tour_date.discount)}% {labels.discount}
            </span>
          )
        )}
        <Image
          src={tour?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(tour?.image, 'Tour')}
          height={0}
          width={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="(min-width: 640px) 400px, 80vw"
          priority={priority}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
        />
        {!!tour?.cheapest_tour_date?.price_with_vat && (
          <span className="z-1 absolute bottom-2 right-2 rounded-[12px] bg-accent/75 px-4 py-2 text-sm font-semibold text-white lg:text-base">
            {labels.from} {formatTourPrice(tour.cheapest_tour_date.price_with_vat, locale)}{' '}
            {currency}
          </span>
        )}
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-4 bg-white p-4">
        <Title data-ea-visual-tag={Title === 'h3' ? undefined : 'h3'} className="line-clamp-1 text-base font-bold uppercase text-foreground sm:text-lg">
          {tour?.name}
        </Title>
        <div className="flex items-center justify-start gap-2">
          <BiCalendar size={20} className="text-primary" />
          <p className="text-sm font-normal text-primary">
            {tour?.actual_tour_date?.date_start && tour?.actual_tour_date?.date_stop
              ? formatDateRange(
                  tour?.actual_tour_date?.date_start,
                  tour?.actual_tour_date?.date_stop,
                )
              : labels.noDate}
          </p>
          {tour?.valid_tour_dates_count && tour?.valid_tour_dates_count > 1 ? (
            <span className="text-sm font-normal text-[#969696]">
              +{tour.valid_tour_dates_count - 1}{' '}
              {getTourTermLabel(tour.valid_tour_dates_count - 1, locale)}
            </span>
          ) : null}
        </div>
        <div className="relative h-10 w-full overflow-hidden">
          {description}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 right-0 h-5 w-16 bg-gradient-to-r from-transparent to-white"
          />
        </div>
        <div className="flex w-full items-center justify-end">
          <Button asChild>
            <span>
              {labels.more} <LuArrowUpRight size={20} />
            </span>
          </Button>
        </div>
      </div>
    </Link>
  );
}
